import * as THREE from 'three';
import { modelLoader } from '../ktx2';
import { afterFirstView } from '../shaderWarmup';
import type { CharacterStyle } from './characterStyle';
import { crowdInView, hitFlash } from './characterLook';
import type { CrowdActor } from './crowd';
import { createSkeleton, JOINT_COUNT, JOINT_PARENTS, type Skeleton } from './skeleton';

/**
 * The Meshy crowd (docs/ASSETS.md, "Crowd bodies"): six textured bodies made in Meshy AI (a man in an
 * overcoat and flat cap, a woman in a coat and headscarf, a docker in a donkey jacket, a young woman in a mini
 * dress, and the gang looks: a rocker in black leather and a skinhead in a Harrington), drawn for every
 * pedestrian at MEDIUM and HIGH. LOW keeps the procedural crowd (crowd.ts).
 *
 * Technique: bone-texture instancing, driven by the SAME procedural animator as the classic crowd. Each body is
 * ONE instanced draw (plus one in the shadow pass) whatever the crowd size. Every frame each visible pedestrian
 * writes its 20 joint matrices (3 × vec4 each, the affine part) into one row of a shared float texture; the
 * vertex shader fetches its four joints by row (iRow) and skins the rest mesh. The build
 * (scripts/build-crowd-models.mjs) already retargeted the mesh to our rig: its vertices are in OUR rest pose
 * and its weights on OUR joints, and the body carries its own joint positions, so the pose goes through a
 * skeleton with the body's proportions (`CrowdBody.skeleton`) and the matrix written is joint · T(-rest).
 * A vertex-animation texture was the alternative; it would bake a few fixed clips and lose what makes the crowd
 * alive (heads turning, flinches, falls in any direction, cowering, the get-up, gait traits) and still cost a
 * texture per clip. Bone textures keep all of it at the cost the crowd already pays (the matrices are computed
 * anyway), and WebGL2 (texelFetch on an RGBA32F texture) is all it needs.
 *
 * Variety: a 256² mask per body marks coat (R), trousers or tights (G), headwear or hair (B) and skin (A); the
 * fragment shader swaps each for the pedestrian's own colour, keeping the cloth's shading (the texel's
 * luminance against the region's reference colour). So a style's palette still dresses the person.
 */

const BASE = import.meta.env?.BASE_URL ?? './';

/**
 * `?classicpeds`: the procedural crowd at every quality — the game as it was before the Meshy bodies, for A/B
 * screenshots and perf read-outs in the same build.
 */
export const CLASSIC_PEDS = typeof location !== 'undefined' && new URLSearchParams(location.search).has('classicpeds');

/** Body ids in the pack, by role. */
export const CROWD_BODY_IDS = ['ped-overcoat', 'ped-woman', 'ped-worker', 'ped-girl', 'ped-rocker', 'ped-skinhead'] as const;
export type CrowdBodyId = (typeof CROWD_BODY_IDS)[number];

export interface CrowdBody {
  readonly id: string;
  /** Rest mesh in character space (height-1 units, feet at y = 0); attributes position, normal, uv, aJoints, aWeights. */
  readonly geometry: THREE.BufferGeometry;
  readonly map: THREE.Texture | null;
  readonly mask: THREE.Texture | null;
  /** Our rig with this body's proportions (joint offsets measured on the model). */
  readonly skeleton: Skeleton;
  /** Rest position of each of our joints (character space), JOINT_COUNT × xyz. */
  readonly jointPos: Float32Array;
  /** Linear-RGB reference colour per tint region: coat, legs, head, skin (12 floats). */
  readonly refs: Float32Array;
  readonly tris: number;
  /** Shadow stand-in (~700 triangles, position + skin): cast instead of the full body. */
  readonly shadow: THREE.BufferGeometry | null;
}

/** Tint slots per pedestrian (linear RGB, 4 × 3 floats): coat, legs, head, skin. */
export const TINT_FLOATS = 12;

/** Which body a style is drawn with (a pure function of the style, so a pedestrian keeps it for life). */
export function crowdBodyFor(style: CharacterStyle): CrowdBodyId {
  const o = style.outfit;
  if (style.female) return o === 'miniSkirt' || (o === 'dressSkirt' && (style.age ?? 0.3) < 0.35) ? 'ped-girl' : 'ped-woman';
  // The youth gangs (the only styles with real swagger): rockers in leather, skinheads and mods in the
  // Harrington body. Everyone else in a short jacket is drawn as the docker.
  if ((style.swagger ?? 0) >= 0.5) return style.hair === 'quiff' ? 'ped-rocker' : 'ped-skinhead';
  if (o === 'donkeyJacket' || o === 'shirtsleeves' || o === 'knit' || o === 'workCoat' || o === 'harrington' || o === 'parka') return 'ped-worker';
  if (o === 'jacket' && !style.tie && style.headwear === 'none') return 'ped-worker';
  return 'ped-overcoat';
}

const tc = new THREE.Color();
/** The style's colours for the body's tint regions, linear RGB, into `out` (TINT_FLOATS). */
export function crowdTint(style: CharacterStyle, body: CrowdBodyId, out: Float32Array): Float32Array {
  const p = style.palette;
  const set = (k: number, hex: number): void => {
    tc.setHex(hex);
    out[k * 3] = tc.r;
    out[k * 3 + 1] = tc.g;
    out[k * 3 + 2] = tc.b;
  };
  set(0, p.coat);
  set(1, p.trousers);
  // The overcoat's flat cap and the woman's headscarf take the headwear colour when the style has headwear,
  // else (a bare head under the model's cap or scarf) the coat's; the other bodies' heads are hair.
  const hatted = body === 'ped-overcoat' || body === 'ped-woman';
  set(2, hatted ? (style.headwear !== 'none' ? p.headwear : body === 'ped-woman' ? p.headwear : p.coat) : p.hair);
  set(3, p.skin);
  return out;
}

// --- loading -----------------------------------------------------------------------------------------

let loading: Promise<CrowdBody[] | null> | null = null;
let loaded: CrowdBody[] | null = null;

/** The bodies once loaded (sync), else null. */
export function crowdBodies(): CrowdBody[] | null {
  return loaded;
}

/** Load the crowd pack once (public/models/crowd-pack.glb). Never throws: null keeps the procedural crowd. */
export function loadCrowdBodies(): Promise<CrowdBody[] | null> {
  if (typeof document === 'undefined' || CLASSIC_PEDS) return Promise.resolve(null);
  // after the first view's textures (render/shaderWarmup.ts afterFirstView)
  loading ??= afterFirstView()
    .then(() => modelLoader().loadAsync(`${BASE}models/crowd-pack.glb`))
    .then(async (gltf) => {
      const meshes: THREE.Mesh[] = [];
      gltf.scene.traverse((o) => {
        if ((o as THREE.Mesh).isMesh) meshes.push(o as THREE.Mesh);
      });
      const bodies: CrowdBody[] = [];
      const shadows = new Map<string, THREE.BufferGeometry>();
      for (const m of meshes) {
        const x = m.userData as { shadowOf?: string };
        if (x.shadowOf) shadows.set(x.shadowOf, m.geometry);
      }
      for (const m of meshes) {
        const x = m.userData as { id?: string; mask?: number; joints?: number[]; refs?: Record<string, number[] | null>; tris?: number };
        (m.material as THREE.Material).dispose();
        if (!x.id || !x.joints) continue;
        const mat = m.material as THREE.MeshStandardMaterial;
        const mask = x.mask !== undefined ? ((await gltf.parser.getDependency('texture', x.mask)) as THREE.Texture) : null;
        if (mask) mask.colorSpace = THREE.NoColorSpace;
        bodies.push(makeBody(x.id, m.geometry, mat.map, mask, Float32Array.from(x.joints), x.refs ?? {}, x.tris ?? 0, shadows.get(x.id) ?? null));
      }
      loaded = bodies.length ? bodies : null;
      return loaded;
    })
    .catch(() => null);
  return loading;
}

/** A body from its parts (exported for tests). */
export function makeBody(id: string, src: THREE.BufferGeometry, map: THREE.Texture | null, mask: THREE.Texture | null, jointPos: Float32Array, refs: Record<string, number[] | null | undefined>, tris: number, shadowSrc: THREE.BufferGeometry | null = null): CrowdBody {
  const rename = (g: THREE.BufferGeometry): THREE.BufferGeometry => {
    const out = new THREE.BufferGeometry();
    out.setIndex(g.index);
    for (const [from, to] of [
      ['position', 'position'],
      ['normal', 'normal'],
      ['uv', 'uv'],
      ['skinIndex', 'aJoints'],
      ['skinWeight', 'aWeights'],
    ] as const) {
      const a = g.getAttribute(from);
      if (a) out.setAttribute(to, a);
    }
    return out;
  };
  const geometry = rename(src);
  const shadow = shadowSrc ? rename(shadowSrc) : null;
  const base = createSkeleton('average', 1);
  const rest = new Float32Array(JOINT_COUNT * 3);
  for (let j = 0; j < JOINT_COUNT; j++) {
    const p = JOINT_PARENTS[j]!;
    for (let k = 0; k < 3; k++) rest[j * 3 + k] = jointPos[j * 3 + k]! - (p < 0 ? 0 : jointPos[p * 3 + k]!);
  }
  const r = new Float32Array(TINT_FLOATS);
  ['coat', 'legs', 'head', 'skin'].forEach((k, i) => r.set(refs[k] ?? [0.2, 0.2, 0.2], i * 3));
  if (map) {
    map.colorSpace = THREE.SRGBColorSpace;
    map.anisotropy = 2;
  }
  return { id, geometry, map, mask, skeleton: { ...base, rest }, jointPos, refs: r, tris, shadow };
}

// --- material ------------------------------------------------------------------------------------------

const VERT_DECL = /* glsl */ `
uniform highp sampler2D uBones;
attribute vec4 aJoints;
attribute vec4 aWeights;
attribute float iRow;
mat4 crowdSkin;
mat4 crowdBone(float j) {
  int x = int(j + 0.5) * 3;
  int y = int(iRow + 0.5);
  vec4 r0 = texelFetch(uBones, ivec2(x, y), 0);
  vec4 r1 = texelFetch(uBones, ivec2(x + 1, y), 0);
  vec4 r2 = texelFetch(uBones, ivec2(x + 2, y), 0);
  return mat4(r0.x, r1.x, r2.x, 0.0, r0.y, r1.y, r2.y, 0.0, r0.z, r1.z, r2.z, 0.0, r0.w, r1.w, r2.w, 1.0);
}
mat4 crowdSkinMatrix() {
  return aWeights.x * crowdBone(aJoints.x) + aWeights.y * crowdBone(aJoints.y) + aWeights.z * crowdBone(aJoints.z) + aWeights.w * crowdBone(aJoints.w);
}
`;

const TINT_DECL_V = /* glsl */ `
attribute vec3 iCoat;
attribute vec3 iLegs;
attribute vec3 iHead;
attribute vec3 iSkin;
attribute float iHit;
varying vec3 vCoat;
varying vec3 vLegs;
varying vec3 vHead;
varying vec3 vSkin;
varying float vHit;
`;

/** Hit-flash (characterLook `hitFlash`, per instance): the struck figure pulls towards a warm white for a moment. */
const HIT_FRAG = /* glsl */ `
outgoingLight = mix( outgoingLight, vec3( 1.0, 0.9, 0.72 ), vHit );
#include <opaque_fragment>`;

const TINT_FRAG = /* glsl */ `
#include <map_fragment>
{
  vec4 crowdM = texture2D( uMask, vMapUv );
  float crowdL = dot( diffuseColor.rgb, vec3( 0.2126, 0.7152, 0.0722 ) );
  vec3 crowdC = diffuseColor.rgb;
  crowdC = mix( crowdC, vCoat * min( crowdL / uRefLum.x, 2.2 ), crowdM.r );
  crowdC = mix( crowdC, vLegs * min( crowdL / uRefLum.y, 2.2 ), crowdM.g );
  crowdC = mix( crowdC, vHead * min( crowdL / uRefLum.z, 2.2 ), crowdM.b );
  crowdC = mix( crowdC, vSkin * min( crowdL / uRefLum.w, 1.3 ), crowdM.a );
  diffuseColor.rgb = crowdC;
}
`;

const lum = (r: Float32Array, i: number): number => Math.max(0.012, r[i * 3]! * 0.2126 + r[i * 3 + 1]! * 0.7152 + r[i * 3 + 2]! * 0.0722);

function createBodyMaterials(body: CrowdBody, bones: THREE.Texture): { material: THREE.MeshLambertMaterial; depth: THREE.MeshDepthMaterial } {
  const material = new THREE.MeshLambertMaterial({ map: body.map });
  const uBones = { value: bones };
  const uMask = { value: body.mask };
  const uRefLum = { value: new THREE.Vector4(lum(body.refs, 0), lum(body.refs, 1), lum(body.refs, 2), lum(body.refs, 3)) };
  const tinted = !!body.mask && !!body.map;
  material.onBeforeCompile = (sh) => {
    sh.uniforms.uBones = uBones;
    sh.uniforms.uMask = uMask;
    sh.uniforms.uRefLum = uRefLum;
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', `#include <common>\n${VERT_DECL}${TINT_DECL_V}`)
      .replace('#include <beginnormal_vertex>', 'crowdSkin = crowdSkinMatrix();\nvec3 objectNormal = normalize( mat3( crowdSkin ) * normal );\nvCoat = iCoat; vLegs = iLegs; vHead = iHead; vSkin = iSkin; vHit = iHit;')
      .replace('#include <begin_vertex>', 'vec3 transformed = ( crowdSkin * vec4( position, 1.0 ) ).xyz;');
    sh.fragmentShader = sh.fragmentShader.replace('#include <common>', '#include <common>\nvarying float vHit;').replace('#include <opaque_fragment>', HIT_FRAG);
    if (tinted)
      sh.fragmentShader = sh.fragmentShader
        .replace('#include <common>', '#include <common>\nuniform sampler2D uMask;\nuniform vec4 uRefLum;\nvarying vec3 vCoat;\nvarying vec3 vLegs;\nvarying vec3 vHead;\nvarying vec3 vSkin;')
        .replace('#include <map_fragment>', TINT_FRAG);
  };
  material.customProgramCacheKey = () => `bh-crowd-body:${tinted ? 1 : 0}`;
  const depth = new THREE.MeshDepthMaterial({ depthPacking: THREE.RGBADepthPacking });
  depth.onBeforeCompile = (sh) => {
    sh.uniforms.uBones = uBones;
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', `#include <common>\n${VERT_DECL}`)
      .replace('#include <begin_vertex>', 'crowdSkin = crowdSkinMatrix();\nvec3 transformed = ( crowdSkin * vec4( position, 1.0 ) ).xyz;');
  };
  depth.customProgramCacheKey = () => 'bh-crowd-body-depth';
  return { material, depth };
}

/** Main-pass material of the shadow stand-ins: nothing reaches the screen. */
const hidden = new THREE.ShaderMaterial({ vertexShader: 'void main() { gl_Position = vec4( 0.0, 0.0, 2.0, 1.0 ); }', fragmentShader: 'void main() { gl_FragColor = vec4( 0.0 ); }', depthWrite: false, colorWrite: false });

// --- renderer ------------------------------------------------------------------------------------------

/** Texels per joint (the 3 × 4 affine part, one row each) and texture width (power of two). */
const TEXELS = 3;
const WIDTH = 64;

interface BodyMesh {
  body: CrowdBody;
  mesh: THREE.Mesh;
  geometry: THREE.InstancedBufferGeometry;
  /** The shadow stand-in (same instances), or null: the body casts its own shadow. */
  shadowGeometry: THREE.InstancedBufferGeometry | null;
  row: Float32Array;
  tint: Float32Array[];
  /** Hit-flash per instance (0..1). */
  hit: Float32Array;
  attrs: THREE.InstancedBufferAttribute[];
  n: number;
}

/**
 * Draws crowd actors with the Meshy bodies: one instanced mesh per body (+ its shadow pass), all reading one
 * joint-matrix texture (a row per drawn actor, `capacity` rows). The caller poses each actor through the body's
 * skeleton (CrowdActor.skel) so its cached matrices (a.jm) fit the mesh. Nothing allocates per frame.
 */
export class MeshyCrowdRenderer {
  private readonly bones: THREE.DataTexture;
  private readonly data: Float32Array;
  private readonly meshes: BodyMesh[] = [];
  private readonly mats: THREE.Material[] = [];
  private rows = 0;

  constructor(
    readonly group: THREE.Group,
    readonly bodies: readonly CrowdBody[],
    private readonly capacity: number,
  ) {
    this.data = new Float32Array(WIDTH * capacity * 4);
    this.bones = new THREE.DataTexture(this.data, WIDTH, capacity, THREE.RGBAFormat, THREE.FloatType);
    this.bones.minFilter = this.bones.magFilter = THREE.NearestFilter;
    this.bones.generateMipmaps = false;
    this.bones.needsUpdate = true;
    const instanced = (src: THREE.BufferGeometry): THREE.InstancedBufferGeometry => {
      const g = new THREE.InstancedBufferGeometry();
      g.setIndex(src.index);
      for (const name of ['position', 'normal', 'uv', 'aJoints', 'aWeights']) {
        const a = src.getAttribute(name);
        if (a) g.setAttribute(name, a);
      }
      g.instanceCount = 0;
      return g;
    };
    for (const body of bodies) {
      const geometry = instanced(body.geometry);
      const attrs: THREE.InstancedBufferAttribute[] = [];
      const inst = (name: string, size: number): Float32Array => {
        const a = new THREE.InstancedBufferAttribute(new Float32Array(capacity * size), size);
        a.setUsage(THREE.DynamicDrawUsage);
        geometry.setAttribute(name, a);
        attrs.push(a);
        return a.array as Float32Array;
      };
      const row = inst('iRow', 1);
      const tint = ['iCoat', 'iLegs', 'iHead', 'iSkin'].map((n) => inst(n, 3));
      const hit = inst('iHit', 1);
      geometry.instanceCount = 0;
      const { material, depth } = createBodyMaterials(body, this.bones);
      this.mats.push(material, depth);
      const mesh = new THREE.Mesh(geometry, material);
      mesh.name = `crowd-${body.id}`;
      mesh.frustumCulled = false;
      mesh.visible = false;
      mesh.matrixAutoUpdate = false;
      group.add(mesh);
      // Shadows: the ~700-triangle stand-in casts them. Its main-pass draw puts every vertex behind the far
      // plane (no fragments, a trivial vertex shader); only its depth pass, skinned like the body, draws.
      let shadowGeometry: THREE.InstancedBufferGeometry | null = null;
      if (body.shadow) {
        shadowGeometry = instanced(body.shadow);
        shadowGeometry.setAttribute('iRow', geometry.getAttribute('iRow'));
        const proxy = new THREE.Mesh(shadowGeometry, hidden);
        proxy.name = `crowd-${body.id}-shadow`;
        proxy.frustumCulled = false;
        proxy.castShadow = true;
        proxy.customDepthMaterial = depth;
        proxy.matrixAutoUpdate = false;
        mesh.add(proxy);
      } else {
        mesh.castShadow = true;
        mesh.customDepthMaterial = depth;
      }
      this.meshes.push({ body, mesh, geometry, shadowGeometry, row, tint, hit, attrs, n: 0 });
    }
  }

  /** Index of body `id` in this renderer, or -1. */
  indexOf(id: string): number {
    return this.meshes.findIndex((m) => m.body.id === id);
  }

  begin(): void {
    this.rows = 0;
    for (const m of this.meshes) m.n = 0;
  }

  /**
   * Draw actor `a` (posed through `bodies[body].skeleton`) at (x, y, z) with tint colours `tint`
   * (TINT_FLOATS, linear). Off screen or over capacity: skipped.
   */
  addActor(a: CrowdActor, body: number, tint: Float32Array, x: number, y: number, z: number): void {
    const bm = this.meshes[body];
    if (!bm || this.rows >= this.capacity || !crowdInView(x, y, z)) return;
    const r = this.rows++;
    const dx = x - a.jx;
    const dy = y - a.jy;
    const dz = z - a.jz;
    writeBoneRow(a.jm, bm.body.jointPos, this.data, r * WIDTH * 4, dx, dy, dz);
    const i = bm.n++;
    bm.row[i] = r;
    bm.hit[i] = hitFlash(a.hitT, a.hitStrength);
    for (let k = 0; k < 4; k++) {
      const t = bm.tint[k]!;
      t[i * 3] = tint[k * 3]!;
      t[i * 3 + 1] = tint[k * 3 + 1]!;
      t[i * 3 + 2] = tint[k * 3 + 2]!;
    }
  }

  end(): void {
    for (const m of this.meshes) {
      m.geometry.instanceCount = m.n;
      if (m.shadowGeometry) m.shadowGeometry.instanceCount = m.n;
      m.mesh.visible = m.n > 0;
      if (!m.n) continue;
      for (const a of m.attrs) {
        a.clearUpdateRanges();
        a.addUpdateRange(0, m.n * a.itemSize);
        a.needsUpdate = true;
      }
    }
    if (this.rows) this.bones.needsUpdate = true;
  }

  /** Draw calls (main pass, per body; each also has a shadow draw), triangles drawn and shadow triangles last frame. */
  cost(): { calls: number; tris: number; shadowTris: number; instances: number } {
    let calls = 0;
    let shadowTris = 0;
    let tris = 0;
    let instances = 0;
    for (const m of this.meshes) {
      if (!m.n) continue;
      calls++;
      tris += m.n * m.body.tris;
      shadowTris += m.n * (m.shadowGeometry ? (m.shadowGeometry.index?.count ?? 0) / 3 : m.body.tris);
      instances += m.n;
    }
    return { calls, tris, shadowTris, instances };
  }

  dispose(): void {
    for (const m of this.meshes) {
      m.mesh.removeFromParent();
      m.geometry.dispose();
      m.shadowGeometry?.dispose();
    }
    hidden.dispose();
    for (const m of this.mats) m.dispose();
    this.bones.dispose();
  }
}

/**
 * One texture row: per joint j the affine rows of M_j · T(-p_j) (M_j = the actor's cached joint matrix shifted by
 * (dx, dy, dz), p_j = the body's rest position of joint j), 3 texels of xyzw each. Exported for tests.
 */
export function writeBoneRow(jm: Float32Array, jointPos: Float32Array, out: Float32Array, o: number, dx: number, dy: number, dz: number): void {
  for (let j = 0; j < JOINT_COUNT; j++) {
    const m = j * 16;
    const px = jointPos[j * 3]!;
    const py = jointPos[j * 3 + 1]!;
    const pz = jointPos[j * 3 + 2]!;
    const t = o + j * TEXELS * 4;
    for (let r = 0; r < 3; r++) {
      const a = jm[m + r]!;
      const b = jm[m + 4 + r]!;
      const c = jm[m + 8 + r]!;
      out[t + r * 4] = a;
      out[t + r * 4 + 1] = b;
      out[t + r * 4 + 2] = c;
      out[t + r * 4 + 3] = jm[m + 12 + r]! + (r === 0 ? dx : r === 1 ? dy : dz) - (a * px + b * py + c * pz);
    }
  }
}
